import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { Location, NgIf, NgFor, AsyncPipe } from '@angular/common';
import { AfterViewInit, Component, OnInit, TemplateRef, ViewChild, inject } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatFormField, MatError } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSelectChange, MatSelect, MatOption } from '@angular/material/select';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { RootState } from '@next/core-lib/app';
import { PageDetailComponent, SummaryCardComponent } from '@next/core-lib/components';
import { DialogService } from '@next/core-lib/dialog';
import { LocaleService, TranslatePipe, AgrDatePipe } from '@next/core-lib/i18n';
import { RouterSelectors } from '@next/core-lib/router-store';
import { TableColumnTypes, TableConfig, TableComponent } from '@next/core-lib/table';
import { AgrUser, UserSelectors } from '@next/core-lib/user';
import { select, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { map, take, withLatestFrom } from 'rxjs/operators';
import { AdditiveRow, ProductRow } from 'src/app/feed-advice/models/feed-advice.models';
import { Product } from 'src/app/shared/models/shared.model';

import { AdditiveComponent } from '../../../shared/components/additive/additive.component';
import { ReferenceScheme, ViewMode } from '../../models/reference-scheme.model';
import * as referenceSchemeActions from '../../store/reference-scheme.actions';
import {
  getActiveAdditives,
  getActiveProducts,
  getSelectedCountry,
} from '../../store/reference-scheme.selectors';

@Component({
  selector: 'app-create-reference-scheme',
  templateUrl: './create-reference-scheme.component.html',
  styleUrls: ['./create-reference-scheme.component.scss'],
  imports: [
    PageDetailComponent,
    ReactiveFormsModule,
    NgIf,
    MatFormField,
    MatInput,
    MatError,
    TableComponent,
    MatSelect,
    NgFor,
    MatOption,
    MatButton,
    AdditiveComponent,
    SummaryCardComponent,
    AsyncPipe,
    TranslatePipe,
    AgrDatePipe,
  ],
})
export class CreateReferenceSchemeComponent implements AfterViewInit, OnInit {
  private readonly store = inject<Store<RootState>>(Store);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private location = inject(Location);
  private readonly locale = inject<LocaleService>(LocaleService);
  private dialogService = inject(DialogService);

  selectedCountry$: Observable<string> = this.store.select(getSelectedCountry);

  routeUrl$: Observable<string> = this.store.pipe(select(RouterSelectors.getPath));

  viewMode$: Observable<ViewMode> = this.routeUrl$.pipe(
    map((url: string) => {
      if (url.indexOf('reference-scheme/view/') >= 0) {
        return ViewMode.View;
      } else if (url.endsWith('/reference-scheme/create')) {
        return ViewMode.Create;
      } else {
        return ViewMode.Edit;
      }
    }),
  );

  pageTitle$: Observable<string> = this.viewMode$.pipe(
    map((viewMode: ViewMode) => {
      switch (viewMode) {
        case ViewMode.View:
          return `${this.locale.translate('reference-scheme.create.title')}: ${
            this.referenceScheme.name
          } `;
        case ViewMode.Create:
          return `${this.locale.translate('add')} ${this.locale.translate(
            'reference-scheme.create.title',
          )}`;
        case ViewMode.Edit:
          return `${this.locale.translate('edit')} ${this.locale.translate(
            'reference-scheme.create.title',
          )}`;
      }
    }),
  );

  public viewModes: typeof ViewMode = ViewMode;
  products$: Observable<Product[]> = this.store.select(getActiveProducts);
  additives$: Observable<Product[]> = this.store.select(getActiveAdditives);
  user$: Observable<AgrUser> = this.store.select(UserSelectors.selectUser);

  productControls = new FormArray([]);
  chickenWeightControls = new FormArray([]);
  ageFromControls = new FormArray([]);
  ageToControls = new FormArray([]);
  totalFeedControls = new FormArray([]);

  /* TEMPLATES */
  @ViewChild('productNameTemplate') productNameTemplate: TemplateRef<Product>;

  /* TABLE CONFIG */
  tableConfig: TableConfig = {
    dataSource: new MatTableDataSource([] as ReferenceScheme[]),
    selectColumn: false,
    pagination: false,
    columns: [],
  };

  referenceSchemeGroup = new FormGroup({
    name: new FormControl(null, [Validators.required]),
    description: new FormControl(null),
  });

  isNewReferenceScheme: boolean;
  referenceScheme: ReferenceScheme;

  additiveRows: AdditiveRow[] = [];
  showAdditives = true;

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {}

  ngOnInit(): void {
    this.activatedRoute.data
      .pipe(withLatestFrom(this.viewMode$), take(1))
      .subscribe(([response, viewMode]) => {
        if (viewMode === ViewMode.View) {
          this.tableConfig = {
            dataSource: new MatTableDataSource([] as ReferenceScheme[]),
            selectColumn: false,
            pagination: false,
            columns: [
              {
                id: 'productName',
                type: TableColumnTypes.TEMPLATE,
                label: this.locale.translate('reference-scheme.create.table-header.productName'),
              },
              {
                id: 'chickenWeightInGrams',
                type: TableColumnTypes.STRING,
                label: this.locale.translate(
                  'reference-scheme.create.table-header.chickenWeightInGrams',
                ),
              },
              {
                id: 'ageFrom',
                type: TableColumnTypes.STRING,
                label: this.locale.translate('reference-scheme.create.table-header.ageFrom'),
              },
              {
                id: 'ageTo',
                type: TableColumnTypes.STRING,
                label: this.locale.translate('reference-scheme.create.table-header.ageTo'),
              },
              {
                id: 'totalFeed',
                type: TableColumnTypes.STRING,
                label: this.locale.translate('reference-scheme.create.table-header.totalFeed'),
              },
              {
                id: 'coreFeed',
                type: TableColumnTypes.STRING,
                label: this.locale.translate('reference-scheme.create.table-header.coreFeed'),
              },
              {
                id: 'wheatFeed',
                type: TableColumnTypes.STRING,
                label: this.locale.translate('reference-scheme.create.table-header.wheatFeed'),
              },
              {
                id: 'comment',
                type: TableColumnTypes.STRING,
                label: this.locale.translate('reference-scheme.create.table-header.comment'),
              },
            ],
            scrolling: true,
            width: '1200px',
            wrapperWidth: '100%',
            dragDrop: false,
          };
        } else {
          this.tableConfig = {
            dataSource: new MatTableDataSource([] as ReferenceScheme[]),
            selectColumn: false,
            pagination: false,
            columns: [
              {
                id: 'productName',
                type: TableColumnTypes.TEMPLATE,
                label: this.locale.translate('reference-scheme.create.table-header.productName'),
                inputConfig: {
                  formArray: this.productControls,
                  options: [Validators.required],
                },
              },
              {
                id: 'chickenWeightInGrams',
                type: TableColumnTypes.INPUT_NUMBER,
                inputConfig: {
                  step: 2,
                  formArray: this.chickenWeightControls,
                  options: [Validators.required],
                  callback: (row: ProductRow, input) =>
                    this.editProductRow(row, 'chickenWeightInGrams', <string>input.value),
                },
                label: this.locale.translate(
                  'reference-scheme.create.table-header.chickenWeightInGrams',
                ),
              },
              {
                id: 'ageFrom',
                type: TableColumnTypes.INPUT_NUMBER,
                inputConfig: {
                  step: 2,
                  formArray: this.ageFromControls,
                  options: [Validators.required],
                  callback: (row: ProductRow, input) =>
                    this.editProductRow(row, 'ageFrom', <string>input.value),
                },
                label: this.locale.translate('reference-scheme.create.table-header.ageFrom'),
              },
              {
                id: 'ageTo',
                type: TableColumnTypes.INPUT_NUMBER,
                inputConfig: {
                  step: 2,
                  formArray: this.ageToControls,
                  callback: (row: ProductRow, input) =>
                    this.editProductRow(row, 'ageTo', <string>input.value),
                },
                label: this.locale.translate('reference-scheme.create.table-header.ageTo'),
              },
              {
                id: 'totalFeed',
                type: TableColumnTypes.INPUT_NUMBER,
                inputConfig: {
                  step: 2,
                  formArray: this.totalFeedControls,
                  options: [Validators.required],
                  callback: (row: ProductRow, input) =>
                    this.editProductRow(row, 'totalFeed', <string>input.value),
                },
                label: this.locale.translate('reference-scheme.create.table-header.totalFeed'),
              },
              {
                id: 'coreFeed',
                type: TableColumnTypes.INPUT_NUMBER,
                inputConfig: {
                  step: 2,
                  formArray: new FormArray([]),
                  callback: (row: ProductRow, input) =>
                    this.editProductRow(row, 'coreFeed', <string>input.value),
                },
                label: this.locale.translate('reference-scheme.create.table-header.coreFeed'),
              },
              {
                id: 'wheatFeed',
                type: TableColumnTypes.INPUT_NUMBER,
                inputConfig: {
                  step: 2,
                  formArray: new FormArray([]),
                  callback: (row: ProductRow, input) =>
                    this.editProductRow(row, 'wheatFeed', <string>input.value),
                },
                label: this.locale.translate('reference-scheme.create.table-header.wheatFeed'),
              },
              {
                id: 'comment',
                type: TableColumnTypes.INPUT_STRING,
                inputConfig: {
                  formArray: new FormArray([]),
                  callback: (row: ProductRow, input) =>
                    this.editProductRow(row, 'comment', <string>input.value),
                },
                label: this.locale.translate('reference-scheme.create.table-header.comment'),
              },
            ],
            actions: [
              {
                id: 'delete',
                label: '',
                callback: (row: ProductRow) => this.deleteProductRow(row),
                icon: 'delete',
                disabled: () => false,
              },
            ],
            scrolling: true,
            width: '1200px',
            wrapperWidth: '100%',
            dragDrop: true,
            dragDropped: (event, row: ProductRow) => this.dragDropProductRow(event, row),
            enableArrowKeys: true,
          };
        }
        // eslint-disable-next-line
        if (response.hasOwnProperty('referenceScheme')) {
          this.isNewReferenceScheme = false;
          this.referenceScheme = { ...(<ReferenceScheme>response.referenceScheme) };
          this.referenceSchemeGroup.controls.name.setValue(this.referenceScheme.name);
          this.referenceSchemeGroup.controls.description.setValue(this.referenceScheme.description);

          this.tableConfig.dataSource.data = this.referenceScheme.productRows
            .map((p) => Object.assign({}, p))
            .sort((a, b) => (a.id > b.id ? 1 : -1));
        } else {
          this.isNewReferenceScheme = true;
          this.referenceScheme = <ReferenceScheme>{};

          this.tableConfig.dataSource.data = [
            {
              id: 0,
              product: null,
              chickenWeightInGrams: null,
              ageFrom: null,
              ageTo: null,
              totalFeed: null,
              coreFeed: null,
              wheatFeed: null,
              comment: null,
            },
          ];
        }
      });
  }

  ngAfterViewInit(): void {
    // Set productname column template.
    setTimeout(() => {
      const newConfig = { ...this.tableConfig };
      const columnProductName = newConfig.columns.find((c) => c.id === 'productName');

      columnProductName.template = this.productNameTemplate;
      this.tableConfig = newConfig;
    }, 50);
  }

  selectProduct(selection: MatSelectChange, productRow: ProductRow) {
    this.productControls.controls[productRow.id].setValue(<Product>selection.value);
    productRow.product = <Product>selection.value;
  }

  getProduct(row: ProductRow): Product {
    let foundProduct = <Product>{};
    if (row.product) {
      this.products$.pipe(take(1)).subscribe((products) => {
        foundProduct = !!products && products.find((product) => product.code === row.product.code);
      });
    }
    const productControl = new FormControl();
    productControl.setValue(foundProduct);

    if (
      this.productControls.controls[row.id] === null ||
      this.productControls.controls[row.id] === undefined
    ) {
      this.productControls.setControl(row.id, productControl);
    } else {
      this.productControls.removeAt(row.id);
      this.productControls.setControl(row.id, productControl);
    }

    return foundProduct;
  }

  addProductRow() {
    this.tableConfig.dataSource.data = [
      ...this.tableConfig.dataSource.data,
      {
        id:
          this.tableConfig.dataSource.data.length > 0
            ? Math.max(...this.tableConfig.dataSource.data.map((row: ProductRow) => row.id)) + 1
            : 0,
        product: null,
        chickenWeightInGrams: null,
        ageFrom: null,
        ageTo: null,
        totalFeed: null,
        coreFeed: null,
        wheatFeed: null,
        comment: null,
      },
    ];

    this.applyValidators();
  }

  editProductRow(productRow: ProductRow, element: string, input: string | number) {
    productRow[element as keyof ProductRow] = input as never;
    if (element === 'ageFrom') {
      this.ageToControls.controls[productRow.id].setValidators([
        Validators.required,
        Validators.min(input as number),
      ]);
      this.ageToControls.controls[productRow.id].updateValueAndValidity();
    } else if (element === 'ageTo') {
      this.ageFromControls.controls[productRow.id].setValidators(Validators.max(input as number));
      this.ageFromControls.controls[productRow.id].updateValueAndValidity();
    }
  }

  deleteProductRow(productRow: ProductRow) {
    const productList = <ProductRow[]>[];
    let index = 0;
    // After filtering selected row out, the remaining rows get new ids starting with 0
    // because otherwise problems will arise if the user should start drag/dropping rows.
    [
      ...this.tableConfig.dataSource.data.filter((row: ProductRow) => row.id !== productRow.id),
    ].forEach((row: ProductRow) => {
      const newRow = <ProductRow>{
        ...row,
        id: index,
      };
      productList.push(newRow);
      index = index + 1;
    });
    this.tableConfig.dataSource.data = productList.sort((a: ProductRow, b: ProductRow) =>
      a.id > b.id ? 1 : -1,
    );
    this.applyValidators();
  }

  dragDropProductRow(event: CdkDragDrop<unknown>, productRow: ProductRow) {
    const productList = <ProductRow[]>[];
    let index = 0;
    const filtered = this.tableConfig.dataSource.data.filter(
      (row: ProductRow) => row.id !== event.previousIndex,
    );
    const draggedRow = <ProductRow>{ ...productRow, id: event.currentIndex };
    // the draggedRow is put in its selected place in the array
    filtered.splice(event.currentIndex, 0, draggedRow);
    // the whole array gets new ids if needed, so that they are in ascending order with no doubles
    filtered.forEach((row: ProductRow) => {
      const newRow = <ProductRow>{
        ...row,
        id: index,
      };
      productList.push(newRow);
      index = index + 1;
    });
    this.tableConfig.dataSource.data = productList.sort((a: ProductRow, b: ProductRow) =>
      a.id > b.id ? 1 : -1,
    );

    this.applyValidators();
  }

  back(saved = false) {
    if (this.referenceScheme && saved) {
      // Simply go back to the previous page, which is the balance screen of the cycle.
      this.location.back();
    } else {
      if (this.router.navigated) {
        this.location.back();
      } else {
        this.router.navigate(['../'], { relativeTo: this.activatedRoute });
      }
    }
  }

  applyValidators() {
    this.tableConfig?.dataSource?.data?.forEach((p: ProductRow) => {
      if (!!this.ageToControls.controls[p.id] && p.id < this.ageToControls.length) {
        if (p.id === this.ageToControls.length - 1) {
          this.ageToControls.controls[p.id].setValidators(Validators.min(p.ageFrom as number));
        } else {
          this.ageToControls.controls[p.id].setValidators([
            Validators.required,
            Validators.min(p.ageFrom as number),
          ]);
        }
        this.ageToControls.controls[p.id].updateValueAndValidity();
      }
      if (!!this.ageFromControls.controls[p.id] && p.id < this.ageFromControls.length) {
        this.ageFromControls.controls[p.id].setValidators([
          Validators.required,
          Validators.max(p.ageTo as number),
        ]);
        this.ageFromControls.controls[p.id].updateValueAndValidity();
      }
    });
  }

  private validateReferenceScheme(): boolean {
    this.applyValidators();
    this.referenceSchemeGroup.markAllAsTouched();
    this.ageToControls.controls.forEach((c) => c.updateValueAndValidity());
    this.chickenWeightControls.controls.forEach((c) => c.updateValueAndValidity());
    this.chickenWeightControls.markAllAsTouched();
    this.ageFromControls.controls.forEach((c) => c.updateValueAndValidity());
    this.ageFromControls.markAllAsTouched();
    this.productControls.controls.forEach((c) => {
      if (!!c.value && Object.keys(c.value).length === 0) {
        c.setErrors({ required: true });
      }
    });
    this.productControls.markAllAsTouched();
    this.totalFeedControls.controls.forEach((c) => c.updateValueAndValidity());
    this.totalFeedControls.markAllAsTouched();

    if (
      this.referenceSchemeGroup.valid &&
      this.ageFromControls.controls.every((c) => c.valid) &&
      this.ageToControls.controls.every((c) => c.valid) &&
      this.chickenWeightControls.controls.every((c) => c.valid) &&
      this.productControls.controls.every((c) => c.valid) &&
      this.totalFeedControls.controls.every((c) => c.valid)
    ) {
      return true;
    }
    let description = '';
    if (!this.referenceSchemeGroup.valid) {
      description += `<li>${this.locale.translate(
        'feed-advice.create.validation-error.feedadvice-name',
      )}</li>`;
    }
    if (!this.ageFromControls.controls.every((c) => c.valid)) {
      // eslint-disable-next-line
      if (this.ageFromControls.controls.some((c) => c.errors?.hasOwnProperty('max'))) {
        description += `<li>${this.locale.translate(
          'feed-advice.create.validation-error.age-min-max',
        )}</li>`;
      } else {
        description += `<li>${this.locale.translate(
          'feed-advice.create.validation-error.age-from',
        )}</li>`;
      }
    }

    if (!this.ageToControls.controls.every((c) => c.valid)) {
      // eslint-disable-next-line
      if (this.ageToControls.controls.some((c) => c.errors?.hasOwnProperty('min'))) {
        description += `<li>${this.locale.translate(
          'feed-advice.create.validation-error.age-min-max',
        )}</li>`;
      } else {
        description += `<li>${this.locale.translate(
          'feed-advice.create.validation-error.age-to',
        )}</li>`;
      }
    }
    if (!this.chickenWeightControls.controls.every((c) => c.valid)) {
      description += `<li>${this.locale.translate(
        'feed-advice.create.validation-error.chicken-weight',
      )}</li>`;
    }
    if (!this.productControls.controls.every((c) => c.valid)) {
      description += `<li>${this.locale.translate(
        'feed-advice.create.validation-error.product',
      )}</li>`;
    }
    if (!this.totalFeedControls.controls.every((c) => c.valid)) {
      description += `<li>${this.locale.translate(
        'feed-advice.create.validation-error.total-feed',
      )}</li>`;
    }

    this.dialogService.notify({
      title: this.locale.translate('feed-advice.create.validation-error.title'),
      text: `<ul>${description}</ul>`,
    });

    return false;
  }

  save() {
    if (this.validateReferenceScheme()) {
      this.user$.pipe(take(1)).subscribe((user) => {
        this.selectedCountry$.pipe(take(1)).subscribe((country) => {
          this.referenceScheme = {
            ...this.referenceScheme,
            updatedAdvisorName: user.name,
            createdAdvisorName: this.isNewReferenceScheme
              ? user.name
              : this.referenceScheme.createdAdvisorName,
            name: <string>this.referenceSchemeGroup.controls.name.value,
            description: <string>this.referenceSchemeGroup.controls.description.value,
            productRows: <ProductRow[]>this.tableConfig.dataSource.data.map((row: ProductRow) => ({
              id: row.id,
              product: row.product,
              chickenWeightInGrams: row.chickenWeightInGrams,
              ageFrom: row.ageFrom,
              ageTo: row.ageTo,
              totalFeed: row.totalFeed,
              coreFeed: row.coreFeed,
              wheatFeed: row.wheatFeed,
              comment: row.comment,
            })),
            additiveRows: this.showAdditives ? this.additiveRows : [],
            isReferenceScheme: true,
            country,
            referenceSchemeId: null,
            isActive: true,
          };

          this.store.dispatch(
            referenceSchemeActions.upsertReferenceSchemeRequest({
              referenceScheme: this.referenceScheme,
            }),
          );
        });
      });
    }
  }

  updateAdditiveRows(additiveRows: AdditiveRow[]) {
    this.additiveRows = additiveRows;
  }

  updateShowAdditiveRows(showAdditives: boolean) {
    this.showAdditives = showAdditives;
  }
}
